import React, { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import MigrateShortageSupplierModal from '../../components/modals/MigrateShortageSupplierModal';
import { useActionProps } from '../../utils/useActionProps';
import getIn from '../../components/connectedForm/utils/getIn';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useMutation } from '@apollo/client';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getClientMutationId from '../../utils/getClientMutationId';
import { Analytics } from '../../mixpanel';
import {
  IWorkspaceActionButtonsProps,
  useBasicWorkspaceAction,
  useWorkspaceAction,
  WorkspaceActionButtons,
} from '../../components/Workspace/WorkspaceActionButtons';
import ShortageApprovalMutation from '../../graphql/mutations/purchases/ShortageApprovalMutation.graphql';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { IPurchaseOrder, IPurchaseRequirementDetail } from '../../graphql/schema/p4types';
import WorkspaceModal from '../../components/Workspace/WorkspaceModal';
import ModalContext from '../../components/Context/ModalContext';
import { alternateFullScreenModalStyle } from '../../components/modals/Styles';
import { ContextualMenuItemType } from '@fluentui/react';
import RaisePurchaseOrderFromShortage from '../../graphql/mutations/purchases/RaisePurchaseOrderFromShortage.graphql';
import ShortageDeleteMutation from '../../graphql/mutations/purchases/ShortageDeleteMutation.graphql';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { DocumentNode } from 'graphql';
import { ViewContextProvider } from '../../components/Context/ViewContext';

const LazyPurchaseOrderWorkspace = React.lazy(() => import('../../views/purchases/PurchaseOrderWorkspace'));
const PurchaseOrderFormModal = React.lazy(() => import('../../components/modals/PurchaseOrderFormModal'));

enum PurchaseRequirementDetailWorkspaceAction {
  MigrateSupplier = 'MIGRATE_SHORTAGE_SUPPLIER',
}

/**
 * This is a bit of a weird permissions list (In that there's a bunch more options than this) but all exception 'Migrate Supplier' just use
 * 'PURCHASING_ORDERS_CREATE anyway, so not really a problem.
 */
export const workspaceActionsForPermissions = [
  {
    actionReference: 'PURCHASING_ORDERS_CREATE',
    description: 'Raise Direct Delivery Order',
  },
  {
    actionReference: 'PURCHASING_ORDERS_CREATE',
    description: 'Raise Back to Back Order',
  },
  { actionReference: PurchaseRequirementDetailWorkspaceAction.MigrateSupplier, description: 'Migrate Supplier' },
];

export const workspace = [
  {
    description: 'Raise Direct Delivery Order',
    actionReference: 'PURCHASING_ORDERS_CREATE',
  },
  {
    description: 'Raise Back to Back Order',
    actionReference: 'PURCHASING_ORDERS_CREATE',
  },
];

/**
 * Get actions for raising purchase orders from AbstractPurchaseRequirementDetails.
 * @param raiseMutation   - A mutation matching the current workspace subtype (E.g. 'PurchaseRequirementDetail' or 'PurchaseRequirementDetailPreview').
 * @param directDelivery  - True for Direct Delivery orders. False for Back to Back.
 * @param fullQuantity    - True to raise against the full sales order line quantity (Less assignment quantity). False to raise for the balance only.
 * @param onComplete      - A function to run after a Purchase Order has been created.
 * @param isPreview       - True if this is the preview shortages workspace (Which is always for a single sales order only).
 */
export const useRaisePurchaseOrderAction = (
  raiseMutation: DocumentNode,
  { directDelivery, balanceOnly }: { directDelivery: boolean; balanceOnly: boolean },
  { onComplete, isPreview }: { onComplete: (response: any) => void; isPreview: boolean }
): IWorkspaceAction => {
  const actionProps = useActionProps('purchaseRequirementDetails');
  const condition = () => {
    if (actionProps?.include && actionProps?.purchaseRequirementDetails?.length >= 1) {
      if (!directDelivery) {
        if (!balanceOnly) {
          let restockShortageFound: boolean = false;
          for (const prd of actionProps.purchaseRequirementDetails) {
            if (prd.shortageOrigin !== 'SALES_ORDER') {
              restockShortageFound = true;
              break;
            }
          }
          return !restockShortageFound;
        } else {
          return true;
        }
      } else {
        //Only supports positive inclusions - If all selected requirements are for the same order, allow direct delivery.
        const salesOrderNumber = getIn(
          actionProps,
          'purchaseRequirementDetails.0.salesOrderLine.salesOrder.orderNumber'
        );
        return (
          salesOrderNumber &&
          (!directDelivery ||
            actionProps.purchaseRequirementDetails.every((prd: IPurchaseRequirementDetail) => {
              return (
                prd.shortageOrigin === 'SALES_ORDER' && prd.salesOrderLine?.salesOrder?.orderNumber === salesOrderNumber
              );
            }))
        );
      }
    } else if (actionProps.include && actionProps.purchaseRequirementDetails.length === 0) {
      return true; //Display the button for 0 selections, otherwise it is invisible functionality (outside of certain scenario's).
    } else if (isPreview) {
      //Previews are generated from individual orders only - As such, 'select all' can be valid.
      return true;
    }
  };

  const onError = useComponentQueryErrorHandling();
  const [raise] = useMutation(raiseMutation, {
    variables: {
      input: normalizeVariables({
        clientMutationId: getClientMutationId(),
        supplier: null, //Require setting these on the PO Form.
        warehouse: null, //Require setting these on the PO Form.
        raisePurchaseOrderParameters: {
          directDelivery: directDelivery,
          balanceOnly: balanceOnly,
        },
        ...actionProps,
      }),
    },
    onError: onError,
  });

  const onClick = () => {
    Analytics.track('Raise Purchase Order Shortages', { directDelivery, balanceOnly, isPreview });
    raise().then(onComplete);
  };

  const text = directDelivery ? 'Direct Delivery' : 'Back to Back';
  const secondaryText: string | null = balanceOnly ? '(Shortage Only)' : '(Full Order Quantity)';

  return {
    key: `poAction${directDelivery}${balanceOnly}`,
    text,
    secondaryText,
    condition,
    actionReference: 'PURCHASING_ORDERS_CREATE',
    onClick,
  };
};
const useShortageApproveAction = (approve: boolean): IWorkspaceAction => {
  const actionProps = useActionProps('purchaseRequirementDetails');
  const variables = {
    input: {
      clientMutationId: getClientMutationId(),
      ...normalizeVariables(actionProps),
      approve,
    },
  };
  const onClick = useWorkspaceAction({ mutation: ShortageApprovalMutation, variables });

  return {
    key: `action_shortage_${approve}`,
    text: approve ? 'Approve' : 'Unapprove',
    onClick: onClick,
    actionReference: 'PURCHASING_ORDERS_CREATE',
  };
};
export const useRaisePOActions = ({
  raiseMutation,
  isPreview,
}: {
  raiseMutation: DocumentNode;
  isPreview: boolean;
}): { actionButtons: Array<IWorkspaceAction>; modals: ReactElement } => {
  const { reloadData } = useContext(WorkspaceContext);
  const [purchaseOrderState, setPurchaseOrderState] = useState<{ modalOpen: boolean; initialValues?: IPurchaseOrder }>({
    modalOpen: false,
  });

  const mutationComplete = (response: any) => {
    const purchaseOrder = response?.data?.[Object.keys(response?.data)[0]].result;
    if (purchaseOrder) {
      setPurchaseOrderState({ modalOpen: true, initialValues: purchaseOrder });
    } else {
      console.warn('No PO returned from mutation, could not set purchaseOrderState');
    }
  };

  const directDeliveryFullAction = useRaisePurchaseOrderAction(
    raiseMutation,
    { directDelivery: true, balanceOnly: false },
    { onComplete: mutationComplete, isPreview }
  );
  const directDeliveryBalanceAction = useRaisePurchaseOrderAction(
    raiseMutation,
    { directDelivery: true, balanceOnly: true },
    { onComplete: mutationComplete, isPreview }
  );
  const backToBackFullAction = useRaisePurchaseOrderAction(
    raiseMutation,
    { directDelivery: false, balanceOnly: false },
    { onComplete: mutationComplete, isPreview }
  );
  const backToBackBalanceAction = useRaisePurchaseOrderAction(
    raiseMutation,
    { directDelivery: false, balanceOnly: true },
    { onComplete: mutationComplete, isPreview }
  );

  const onClosePurchaseOrderFormModal = useCallback(() => {
    setPurchaseOrderState((prevState) => ({ ...prevState, modalOpen: false }));
    reloadData();
  }, [reloadData]);

  const [purchaseOrderWorkspaceState, setPurchaseOrderWorkspaceState] = useState({ modalOpen: false });
  const poWorkspaceModal = (
    <WorkspaceModal
      component={LazyPurchaseOrderWorkspace}
      viewScreenName={'/purchases/PurchaseOrderWorkspace/Modal'}
      modalState={purchaseOrderWorkspaceState}
      closeModal={() => {
        setPurchaseOrderWorkspaceState({ modalOpen: false });
      }}
    />
  );

  const formData = useMemo(
    () => ({ defaultRecord: purchaseOrderState.initialValues }),
    [purchaseOrderState.initialValues]
  );

  const poFormModal = (
    <ModalContext.Provider value={{ onClose: onClosePurchaseOrderFormModal, style: alternateFullScreenModalStyle }}>
      <PurchaseOrderFormModal
        onClosePurchaseOrderFormModal={onClosePurchaseOrderFormModal}
        purchaseOrderState={purchaseOrderState}
        formData={formData}
      />
    </ModalContext.Provider>
  );

  const actionButtons = useMemo<Array<IWorkspaceAction>>(() => {
    return [
      {
        key: 'poHeader',
        itemType: ContextualMenuItemType.Header,
        text: 'Raise a Purchase Order',
        actionReference: '-',
        ignorePermissions: true, //This will be seen as a button by WorkspaceActionButtons.js, so it will try to permission check it...
      },
      directDeliveryFullAction,
      directDeliveryBalanceAction,
      isPreview ? backToBackFullAction : null,
      backToBackBalanceAction,
    ].filter((ab) => ab !== null) as Array<IWorkspaceAction>;
  }, [backToBackBalanceAction, backToBackFullAction, directDeliveryBalanceAction, directDeliveryFullAction, isPreview]);

  return {
    actionButtons: actionButtons,
    modals: (
      <>
        {poWorkspaceModal}
        {poFormModal}
      </>
    ),
  };
};

const useMigrateSupplierAction = (): [IWorkspaceAction, ReactElement] => {
  const [modalOpen, setModalOpen] = useState(false);

  return [
    {
      key: 'migrateSupplier',
      text: 'Migrate Supplier',
      actionReference: PurchaseRequirementDetailWorkspaceAction.MigrateSupplier,
      onClick: () => setModalOpen(true),
    },
    <MigrateShortageSupplierModal modalOpen={modalOpen} onClose={() => setModalOpen(false)} />,
  ];
};

export const PurchaseRequirementDetailActions = (props: IWorkspaceActionButtonsProps) => {
  const [migrateSupplierAction, migrateSupplierModal] = useMigrateSupplierAction();

  const { actionButtons: raisePOActions, modals: raisePOModals } = useRaisePOActions({
    raiseMutation: RaisePurchaseOrderFromShortage,
    isPreview: true,
  });

  const approveShortagesAction = useShortageApproveAction(true);
  const unapproveShortagesAction = useShortageApproveAction(false);

  const deleteShortageSupplierLevelAction = useBasicWorkspaceAction({
    mutation: ShortageDeleteMutation,
    errorMessage: 'Could not delete shortages',
    text: 'Delete Shortage Supplier Level',
    type: 'purchaseRequirementDetails',
    actionReference: 'PURCHASING_ORDERS_CREATE',
    additionalNormalizeFields: ['suppliers'], //UI side only additional field for the 'all suppliers' row - Needs to be removed or graphql validation will fail.
  });

  const actionButtons: Array<IWorkspaceAction> = useMemo(() => {
    return [
      migrateSupplierAction,
      approveShortagesAction,
      unapproveShortagesAction,
      deleteShortageSupplierLevelAction,
      ...raisePOActions,
    ];
  }, [
    approveShortagesAction,
    deleteShortageSupplierLevelAction,
    migrateSupplierAction,
    raisePOActions,
    unapproveShortagesAction,
  ]);

  const viewContext = useMemo(
    () => ({
      uniqueName: 'PurchaseRequirementDetailWorkspaceAction',
    }),
    []
  );

  return (
    <>
      <ViewContextProvider value={viewContext}>
        {raisePOModals}
        {migrateSupplierModal}
      </ViewContextProvider>
      <WorkspaceActionButtons {...props} actionButtons={actionButtons} />
    </>
  );
};
